import React, { useMemo } from "react";
import { FiArrowUpRight, FiArrowDownRight, FiMinus } from "react-icons/fi";
import type { BCCADMINBaseModelsExchangeRatesResponse, BCCADMINBaseModelsExchangeRateView } from "../../API/Models";
import { formatRelativeTime } from "../../Helper/dates";

const rateFormatter = new Intl.NumberFormat("fr-FR", { maximumFractionDigits: 2 });
// The handful of currencies that matter for the BCC, in scan order. Only these are shown.
const KEY_CURRENCIES = ["USD", "EUR", "CNY", "GBP", "ZAR"];

/** Small country flag image : flagcdn renders reliably on every OS (unlike emoji flags on Windows). */
const Flag: React.FC<{ code?: string | null }> = ({ code }) => {
	const cc = (code ?? "").trim().toLowerCase();
	if (!/^[a-z]{2}$/.test(cc)) {
		return <span className="flex h-4 w-6 items-center justify-center rounded-[3px] bg-slate-100 text-[8px] font-bold text-slate-400">{(code ?? "?").slice(0, 2).toUpperCase()}</span>;
	}
	return (
		<img
			src={`https://flagcdn.com/h20/${cc}.png`}
			srcSet={`https://flagcdn.com/h40/${cc}.png 2x`}
			alt=""
			width={24}
			height={16}
			className="h-4 w-6 rounded-[3px] object-cover ring-1 ring-slate-200"
			loading="lazy"
			onError={(e) => {
				(e.currentTarget as HTMLImageElement).style.display = "none";
			}}
		/>
	);
};

type Trend = "up" | "down" | "flat";

const FxChip: React.FC<{ rate: BCCADMINBaseModelsExchangeRateView }> = ({ rate }) => {
	// Prefer the 1-day move (the 7-day column is often unpopulated) so the arrow reflects real data.
	const change = rate.change1d ?? rate.change7d ?? null;
	const trend: Trend = change === null || change === 0 ? "flat" : change > 0 ? "up" : "down";
	const tone = trend === "up" ? "text-emerald-600" : trend === "down" ? "text-rose-600" : "text-slate-400";
	const Arrow = trend === "up" ? FiArrowUpRight : trend === "down" ? FiArrowDownRight : FiMinus;
	return (
		<div
			className="flex shrink-0 items-center gap-1.5"
			title={`${rate.currencyName ?? rate.currencyCode} : achat ${rateFormatter.format(rate.buyRate ?? 0)} · vente ${rateFormatter.format(rate.sellRate ?? 0)} CDF`}
		>
			<Flag code={rate.flagCode} />
			<span className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">{rate.currencyCode}</span>
			<span className="text-xs font-bold tabular-nums text-slate-700">{rateFormatter.format(rate.averageRate ?? 0)}</span>
			{change !== null && (
				<span className={`flex items-center gap-0.5 text-[11px] font-semibold tabular-nums ${tone}`}>
					<Arrow className="h-3.5 w-3.5 shrink-0" />
					{(Math.abs(change) * 100).toFixed(2)}%
				</span>
			)}
		</div>
	);
};

export interface FxBarProps {
	data: BCCADMINBaseModelsExchangeRatesResponse | null;
	loading: boolean;
}

/**
 * Discreet exchange-rate strip for the top of the dashboard : no chrome, no label: each key currency
 * shows its flag, code, rate against the CDF, a small green/red tendency curve, and the 7-day delta.
 * Static (no marquee); wraps quietly above the content and stays out of the way.
 */
export const FxBar: React.FC<FxBarProps> = ({ data, loading }) => {
	const rates = useMemo(() => {
		const byCode = new Map<string, BCCADMINBaseModelsExchangeRateView>();
		for (const r of data?.rates ?? []) {
			const code = (r.currencyCode ?? "").toUpperCase();
			if (code && (r.averageRate ?? 0) > 0 && !byCode.has(code)) byCode.set(code, r);
		}
		return KEY_CURRENCIES.map((c) => byCode.get(c)).filter((r): r is BCCADMINBaseModelsExchangeRateView => Boolean(r));
	}, [data]);

	if (loading) {
		return (
			<div className="flex items-center gap-x-6 gap-y-1 px-1">
				{Array.from({ length: 5 }).map((_, i) => (
					<div key={`fxbar-skel-${i}`} className="h-3.5 w-20 animate-pulse rounded bg-slate-100" />
				))}
			</div>
		);
	}
	if (rates.length === 0) return null;

	return (
		<div className="flex flex-nowrap items-center gap-x-6 overflow-hidden whitespace-nowrap px-1">
			{rates.map((r) => (
				<FxChip key={r.currencyCode} rate={r} />
			))}
			{data?.lastSyncedAt && <span className="ml-auto hidden shrink-0 text-[10px] text-slate-300 lg:inline">Sync {formatRelativeTime(data.lastSyncedAt)}</span>}
		</div>
	);
};

export default FxBar;
